import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { BigButton } from "../design/BigButton";

const STAT_LABELS = [
  ["worlds", "Worlds"],
  ["levels", "Levels"],
  ["publishedLevels", "Published"],
  ["draftLevels", "Drafts"],
  ["learningObjects", "Learning objects"],
  ["levelObjects", "Hitboxes"],
  ["rewards", "Rewards"],
  ["parents", "Parents"],
  ["children", "Children"],
  ["supportOpen", "Open support"],
];

export function AdminOverview() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/overview");
        setData(data);
      } catch (e) {
        setErr(formatApiErrorDetail(e.response?.data?.detail || e.message));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <p>Loading overview…</p>;
  if (err) return <div className="admin-error" data-testid="admin-error">{err}</div>;

  const counts = data.counts || {};
  return (
    <div data-testid="admin-overview">
      <div className="admin-header">
        <div>
          <h1>Admin overview</h1>
          <p className="admin-header__sub">System snapshot · {data.sessionsThisWeek || 0} sessions in the last 7 days</p>
        </div>
      </div>

      <div className="admin-stats">
        {STAT_LABELS.map(([key, label]) => (
          <div key={key} className="admin-stat">
            <h3>{label}</h3>
            <p data-testid={`admin-stat-${key}`}>{counts[key] ?? 0}</p>
          </div>
        ))}
      </div>

      <div className="admin-grid-2">
        <section>
          <h2>Top played levels</h2>
          <table className="admin-table" data-testid="admin-top-levels">
            <thead><tr><th>Level</th><th>Attempts</th><th>Completions</th></tr></thead>
            <tbody>
              {(data.topLevels || []).length === 0 && (
                <tr><td colSpan="3" style={{ color: "var(--wmleye-ink-soft)" }}>No completions yet.</td></tr>
              )}
              {(data.topLevels || []).map((l) => (
                <tr key={l.levelId}>
                  <td>{l.name || l.slug}</td>
                  <td>{l.attempts}</td>
                  <td>{l.completions}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        <section>
          <h2>Words needing practice</h2>
          <table className="admin-table" data-testid="admin-stuck-objects">
            <thead><tr><th>Word</th><th>Hints used</th><th>Finds</th></tr></thead>
            <tbody>
              {(data.stuckObjects || []).length === 0 && (
                <tr><td colSpan="3" style={{ color: "var(--wmleye-ink-soft)" }}>No hint-heavy words yet.</td></tr>
              )}
              {(data.stuckObjects || []).map((o) => (
                <tr key={o.learningObjectId}>
                  <td>{o.displayName}</td>
                  <td>{o.hintsUsed}</td>
                  <td>{o.correctFinds}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      </div>

      <div className="admin-grid-2" style={{ marginTop: "1rem" }}>
        <section>
          <h2>Recent audit</h2>
          <table className="admin-table" data-testid="admin-recent-audit">
            <thead><tr><th>When</th><th>Actor</th><th>Action</th></tr></thead>
            <tbody>
              {(data.recentAudit || []).map((a) => (
                <tr key={a.id}>
                  <td><small>{(a.createdAt || "").replace("T", " ").slice(0, 19)}</small></td>
                  <td><small>{a.actorEmail}</small></td>
                  <td><code>{a.action}</code></td>
                </tr>
              ))}
              {(data.recentAudit || []).length === 0 && (
                <tr><td colSpan="3" style={{ color: "var(--wmleye-ink-soft)" }}>Empty.</td></tr>
              )}
            </tbody>
          </table>
        </section>

        <section>
          <h2>Low-token children</h2>
          <table className="admin-table" data-testid="admin-low-tokens">
            <thead><tr><th>Nickname</th><th>Balance</th></tr></thead>
            <tbody>
              {(data.lowTokenChildren || []).length === 0 && (
                <tr><td colSpan="2" style={{ color: "var(--wmleye-ink-soft)" }}>All children have healthy balances.</td></tr>
              )}
              {(data.lowTokenChildren || []).map((c) => (
                <tr key={c.childProfileId}>
                  <td>{c.nickname}</td>
                  <td>{c.tokenBalance}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      </div>
    </div>
  );
}
